import { UserPreferencesSchema, type Role, type UserPreferences } from "@starsach/shared-types";

/**
 * Requirements section 13.1. Preferences come from /v1/me and are applied
 * here: the theme and motion as attributes on <html> that the CSS keys
 * off, the time zone and date format through format(). Nothing here is
 * sensitive, so the theme is also cached in localStorage for the first
 * paint before /v1/me answers.
 */

export const DEFAULT_PREFERENCES: UserPreferences = UserPreferencesSchema.parse({});

const THEME_CACHE_KEY = "starsach.theme";

export function detectedTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

export function effectiveTimeZone(p: Pick<UserPreferences, "timeZone">): string {
  return p.timeZone === "auto" ? detectedTimeZone() : p.timeZone;
}

export function resolveTheme(theme: UserPreferences["theme"]): "light" | "dark" {
  if (theme !== "system") return theme;
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function resolveMotion(motion: UserPreferences["motion"]): "standard" | "reduced" {
  if (motion !== "auto") return motion;
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "reduced" : "standard";
}

/** Writes the resolved theme and motion onto <html>. Returns a disposer that stops following the OS when "system"/"auto". */
export function applyAppearance(p: Pick<UserPreferences, "theme" | "motion">): () => void {
  const root = document.documentElement;
  const paint = () => {
    root.dataset["theme"] = resolveTheme(p.theme);
    root.dataset["motion"] = resolveMotion(p.motion);
  };
  paint();
  try {
    localStorage.setItem(THEME_CACHE_KEY, p.theme);
  } catch {
    // Private mode or a full store: the first paint just follows the OS.
  }
  const queries = [window.matchMedia?.("(prefers-color-scheme: dark)"), window.matchMedia?.("(prefers-reduced-motion: reduce)")].filter(Boolean) as MediaQueryList[];
  for (const q of queries) q.addEventListener("change", paint);
  return () => {
    for (const q of queries) q.removeEventListener("change", paint);
  };
}

/** The role-appropriate landing pages (13.1): what a user can choose to arrive at. */
export function landingPages(role: Role | undefined): Array<{ path: string; label: string }> {
  const common = [
    { path: "/", label: "Focus" },
    { path: "/customers", label: "Customers" },
    { path: "/trusted-advisor", label: "Trusted Advisor" },
  ];
  return role === "internal-team" ? [...common, { path: "/platform", label: "Platform" }] : common;
}

export function isLandingPage(path: string, role: Role | undefined): boolean {
  return landingPages(role).some((l) => l.path === path);
}

const pad = (n: number) => String(n).padStart(2, "0");

function parts(iso: string, timeZone: string): Record<string, string> {
  const f = new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false });
  const out: Record<string, string> = {};
  for (const x of f.formatToParts(new Date(iso))) out[x.type] = x.value;
  // Some engines print midnight as "24".
  if (out["hour"] === "24") out["hour"] = "00";
  return out;
}

/** A timestamp in the user's zone and format. `withTime` false gives the date alone. */
export function formatWhen(iso: string, p: Pick<UserPreferences, "timeZone" | "dateFormat">, withTime = true): string {
  const timeZone = effectiveTimeZone(p);
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  if (p.dateFormat === "auto") {
    return withTime ? d.toLocaleString(undefined, { timeZone }) : d.toLocaleDateString(undefined, { timeZone });
  }
  const x = parts(iso, timeZone);
  const date = p.dateFormat === "iso" ? `${x["year"]}-${x["month"]}-${x["day"]}` : p.dateFormat === "us" ? `${x["month"]}/${x["day"]}/${x["year"]}` : `${x["day"]}/${x["month"]}/${x["year"]}`;
  if (!withTime) return date;
  if (p.dateFormat === "us") {
    const h = Number(x["hour"]);
    return `${date}, ${((h + 11) % 12) + 1}:${x["minute"]} ${h < 12 ? "AM" : "PM"}`;
  }
  return `${date} ${pad(Number(x["hour"]))}:${x["minute"]}`;
}

/** What the cached theme says before /v1/me has answered; the inline script in index.html reads the same key. */
export function cachedTheme(): UserPreferences["theme"] {
  try {
    const t = localStorage.getItem(THEME_CACHE_KEY);
    return t === "light" || t === "dark" ? t : "system";
  } catch {
    return "system";
  }
}

/** Time of day alone, in the user's zone - for a series of events on one known day. */
export function formatTime(iso: string | number, p: Pick<UserPreferences, "timeZone" | "dateFormat">): string {
  const timeZone = effectiveTimeZone(p);
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleTimeString(undefined, { timeZone, hour: "2-digit", minute: "2-digit", hour12: p.dateFormat === "us" ? true : p.dateFormat === "auto" ? undefined : false });
}
